import { User } from 'oidc-client-ts';
import { userManager } from 'app/authUserManager';
import { clearAccessToken } from 'core/auth/accessTokenStorage';

/**
 * « Se connecter en tant que » (admin BO) — grant d'extension OpenIddict
 * `Impersonation` du module Volo Account Pro : on échange le token admin
 * contre un token de l'utilisateur cible, puis on remplace la session OIDC
 * locale. Le cookie SSO du navigateur auprès du serveur d'auth reste celui
 * de l'admin : le retour se fait par un simple signinSilent.
 *
 * Le token impersonné porte les claims `impersonator_userid` /
 * `impersonator_username` (posés par ABP), qui alimentent la bannière.
 */

export interface ImpersonatorInfo {
  userId: string;
  userName?: string;
}

const decodeJwtPayload = (token: string): Record<string, unknown> => {
  const payload = token.split('.')[1];
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(json);
};

/** Impersonateur du token courant, ou null si la session n'est pas impersonnée. */
export const getImpersonator = (accessToken?: string): ImpersonatorInfo | null => {
  if (!accessToken) return null;
  try {
    const claims = decodeJwtPayload(accessToken);
    const userId = claims['impersonator_userid'];
    if (typeof userId !== 'string' || !userId) return null;
    const userName = claims['impersonator_username'];
    return { userId, userName: typeof userName === 'string' ? userName : undefined };
  } catch {
    return null;
  }
};

/** Le state Redux est persisté (redux-persist, sessionStorage de l'onglet) :
 * purge obligatoire à chaque bascule de session, sinon l'autre utilisateur
 * verrait des données en cache. */
const purgePersistedState = () => {
  try {
    sessionStorage.removeItem('persist:root'); // clé de app/store.ts
  } catch {
    // stockage indisponible : le reload rechargera tout depuis l'API
  }
};

const restartApp = () => {
  purgePersistedState();
  // Le token de la session sortante ne doit pas subsister le temps du reload
  // (AbpProvider le réécrit depuis auth.user au boot suivant).
  clearAccessToken();
  window.location.assign('/');
};

/** Sauvegarde de la session admin, restaurée telle quelle au retour : évite de
 * reconstruire les tokens admin via signinRedirect (la machinerie OIDC de l'app
 * — silent-callback no-op, session monitor — échoue et affiche « Session
 * expirée »). Restauration instantanée tant que le token admin n'a pas expiré. */
// Stockée en sessionStorage (par onglet, comme le jeton d'accès) : en localStorage,
// n'importe quel autre onglet non impersonné l'effaçait au démarrage et « Revenir »
// retombait sur le cookie SSO, donc sur l'utilisateur impersonné.
const ADMIN_BACKUP_KEY = 'impersonation.adminUser';

export interface ImpersonationResult {
  isSuccess: boolean;
  errorMessage?: string;
}

/** Bascule la session sur l'utilisateur cible (reload complet de l'app). */
export const impersonateUser = async (targetUserId: string): Promise<ImpersonationResult> => {
  const current = await userManager.getUser();
  if (!current?.access_token) {
    return { isSuccess: false, errorMessage: 'Session courante invalide.' };
  }

  const { authority, client_id: clientId, scope } = userManager.settings;
  const body = new URLSearchParams({
    grant_type: 'Impersonation',
    client_id: clientId,
    scope: scope ?? '',
    access_token: current.access_token,
    UserId: targetUserId,
  });

  let data: {
    access_token?: string;
    refresh_token?: string;
    id_token?: string;
    token_type?: string;
    scope?: string;
    expires_in?: number;
    error?: string;
    error_description?: string;
  };
  try {
    const response = await fetch(`${authority}/connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    data = await response.json();
  } catch {
    return { isSuccess: false, errorMessage: "Le serveur d'authentification est injoignable." };
  }

  if (!data.access_token) {
    return {
      isSuccess: false,
      errorMessage: data.error_description ?? data.error ?? "Échec de l'impersonation.",
    };
  }

  // Le grant d'extension ne renvoie pas toujours d'id_token : les claims de
  // l'access token servent de profil (sub, name, email… y sont). Un token opaque
  // (config ABP reference tokens) n'est pas décodable → échec explicite.
  let profile: Record<string, unknown>;
  try {
    profile = decodeJwtPayload(data.access_token);
  } catch {
    return { isSuccess: false, errorMessage: "Jeton d'impersonation illisible." };
  }

  const user = new User({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    id_token: data.id_token,
    token_type: data.token_type ?? 'Bearer',
    scope: data.scope ?? scope,
    // Pas de fallback long : sans expires_in serveur, on prend une durée courte
    // pour éviter que le client croie le token valide après son expiration réelle.
    expires_at: Math.floor(Date.now() / 1000) + (data.expires_in ?? 300),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    profile: profile as any,
  });

  // Sauvegarde la session admin AVANT de la remplacer, pour la restaurer au retour.
  try {
    sessionStorage.setItem(ADMIN_BACKUP_KEY, current.toStorageString());
  } catch {
    // stockage indisponible : le retour retombera sur removeUser + login RouteGuard
  }

  await userManager.storeUser(user);
  restartApp();
  return { isSuccess: true };
};

/** Retour au compte admin : on restaure la session admin sauvegardée à l'aller
 * (tokens intacts → aucun re-login), puis reload. Si la sauvegarde manque ou est
 * illisible (stockage vidé, token expiré), repli : removeUser + reload, et le
 * RouteGuard de l'app relance le login par le cookie SSO — qui peut ne plus être
 * celui de l'admin (constaté en recette : retour sur le compte impersonné). */
export const stopImpersonation = async (): Promise<void> => {
  const raw = (() => {
    try {
      return sessionStorage.getItem(ADMIN_BACKUP_KEY);
    } catch {
      return null;
    }
  })();

  try {
    if (raw) {
      const adminUser = User.fromStorageString(raw);
      await userManager.storeUser(adminUser);
    } else {
      await userManager.removeUser();
    }
  } catch {
    // Backup illisible ou storeUser en échec : on efface la session impersonnée,
    // le RouteGuard relancera le login (cookie SSO admin → sans mot de passe).
    try {
      await userManager.removeUser();
    } catch {
      // storage indisponible : le reload repartira de zéro
    }
  } finally {
    // Le token admin ne doit JAMAIS survivre au retour, quelle que soit l'issue.
    clearAdminBackup();
  }

  restartApp();
};

/** Efface le token admin sauvegardé. Appelé au retour (finally) et au boot d'une
 * session NON impersonnée : un backup qui traîne (onglet fermé sans « Revenir »,
 * 401 forcé…) exposerait un token admin indéfiniment. La sauvegarde vit en
 * sessionStorage, propre à l'onglet (aller et retour rechargent le même onglet) :
 * un autre onglet non impersonné ne peut donc plus l'effacer. */
export const clearAdminBackup = () => {
  try {
    sessionStorage.removeItem(ADMIN_BACKUP_KEY);
  } catch {
    // storage indisponible : rien à nettoyer
  }
};
